import * as React from "react";
import { TextField, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useForm as ReactHookForm,
  SubmitHandler,
  Controller,
} from "react-hook-form";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { Done, Close } from "@mui/icons-material";

import { FormControl, Typography, Box } from "@mui/material";
import { useForm, FormContextInterface } from "../context";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import Select from "@mui/material/Select";
import { IStudent, dateOfBirth } from "../data/formDefault";
import { useState } from "react";

const parentSchema = yup.object().shape({
  studentSurname: yup.string().required(),
  studentFirstName: yup.string().required(),
  date: yup.string().required(),
  month: yup.string().required(),
  year: yup.string().required(),
  currentYearGroup: yup.string().required(),
  childPronouns: yup.string().required(),
  other1: yup.string(),
  other2: yup.string(),
  other3: yup.string(),
});

const StudentDetails = () => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    if (window.innerWidth < 600) {
      // Only for devices with width < 768px (e.g. mobile)
      setTimeout(() => {
        setOpen(false);
      }, 10000);
    } else {
      setOpen(false);
    }
  };

  const clickmore = () => {
    handleSubmitLogActivity({
      ActivityType: "ChildDetailMoreInfo",
    });
  };

  const {
    termsCondition,
    studentInfo,
    setStudentInfo,
    stepChange,
    handleSubmitLogActivity,
    schoolInfo,
    activeStep,
    setIsValidStudentForm,
    AddPreApplication,
  } = useForm() as FormContextInterface;
  const { dates, months, years } = dateOfBirth();
  const YearGroup = termsCondition.find((item) => item.title === "YearGroup");

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitted },
    control,
    watch,
  } = ReactHookForm<IStudent>({
    resolver: yupResolver(parentSchema),
    mode: "onSubmit",
    defaultValues: studentInfo,
  });

  const childPronouns = watch("childPronouns");

  const isOthersRequired = React.useMemo(() => {
    return childPronouns === "4";
  }, [childPronouns]);

  const other1 = watch("other1");
  const other2 = watch("other2");
  const other3 = watch("other3");

  const isValidForm = React.useMemo(() => {
    if (!isOthersRequired && isValid) {
      return true;
    } else if (
      isValid &&
      isOthersRequired &&
      other1 !== "" &&
      other2 !== "" &&
      other3 !== ""
    ) {
      return true;
    } else return false;
  }, [isOthersRequired, isValid, other1, other2, other3]);

  React.useEffect(() => {
    setIsValidStudentForm(isValidForm);
  }, [isValidForm, setIsValidStudentForm]);

  const onSubmit: SubmitHandler<IStudent> = (data) => {
    if (isValidForm) {
      setStudentInfo(data);
      stepChange("parentDetails");
      handleSubmitLogActivity({
        ActivityType: "ContinueChildDetail",
      });
      var result = {
        id: 0,
        firstName: data.studentFirstName,
        lastName: data.studentSurname,
        schoolId: schoolInfo.id,
      };
      AddPreApplication(result);
    }
  };
  return (
    <Box id="student-content">
      {(schoolInfo?.signUpMethod == "School" &&
        schoolInfo?.schoolSignUp == true) ||
      schoolInfo?.signUpMethod == "MTech" ? (
        <>
          <Box marginY={5}>
            <Typography
              variant="h5"
              marginY={2}
              className="section_titles"
              id="student-details"
              sx={{
                fontSize: "1.1rem !important",
                fontWeight: "bold !important",
              }}
            >
              Please Enter Your Child's Details
            </Typography>
            <form
              onSubmit={handleSubmit(onSubmit)}
              style={{ paddingBottom: "32px" }}
            >
              <FormControl
                variant="outlined"
                fullWidth
                sx={{ "& > :not(style)": { my: 2 } }}
              >
                <Stack direction="row" spacing={2}>
                  <TextField
                    id="standard-helperText"
                    variant="standard"
                    label={
                      <>
                        {"Child's First Name"}
                        <Box component={"span"} sx={{ pl: 0.5, fontSize: 20 }}>
                          *
                        </Box>
                      </>
                    }
                    helperText={
                      errors.studentFirstName && (
                        <Box
                          component={"span"}
                          sx={{ color: "error.main" }}
                        >{`Child's first name is required`}</Box>
                      )
                    }
                    {...register("studentFirstName", { required: true })}
                    fullWidth
                    placeholder="First Name"
                    inputProps={{ maxLength: 100 }}
                  />

                  <TextField
                    id="standard-helperText"
                    variant="standard"
                    label={
                      <>
                        {"Child's Surname"}
                        <Box component={"span"} sx={{ pl: 0.5, fontSize: 20 }}>
                          *
                        </Box>
                      </>
                    }
                    helperText={
                      errors.studentSurname && (
                        <Box
                          component={"span"}
                          sx={{ color: "error.main" }}
                        >{`Child's surname is required`}</Box>
                      )
                    }
                    {...register("studentSurname", { required: true })}
                    fullWidth
                    placeholder="Surname"
                    inputProps={{ maxLength: 50 }}
                  />
                </Stack>
                <Controller
                  name="childPronouns"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth variant="standard">
                      <InputLabel id="my-select-label">
                        Child's Pronouns
                        <Box component={"span"} sx={{ pl: 0.5, fontSize: 20 }}>
                          *
                        </Box>
                      </InputLabel>
                      <Select
                        labelId="my-select-label"
                        id="my-select"
                        {...field}
                      >
                        <MenuItem value="1">He/Him/His</MenuItem>
                        <MenuItem value="2">She/Her/Her</MenuItem>
                        <MenuItem value="3">They/Them/Their</MenuItem>
                        <MenuItem value="4">
                          Other (Please specify below)
                        </MenuItem>
                      </Select>
                      <FormHelperText
                        sx={{ position: "relative" }}
                        tabIndex={-1}
                      >
                        <HtmlTooltip
                          open={open}
                          placement="bottom"
                          onClose={handleClose}
                          id="more-info"
                          title={
                            <React.Fragment>
                              <Typography
                                sx={{
                                  fontStyle: "italic",
                                  padding: "0px 10px",
                                  fontSize: "0.9rem",
                                }}
                                variant="body1"
                                marginY={2}
                                textAlign={"justify"}
                              >
                                At M:Tech we are inclusive and respectful to
                                everybody.
                              </Typography>
                              <Typography
                                sx={{
                                  fontStyle: "italic",
                                  padding: "0px 10px",
                                  fontSize: "0.9rem",
                                }}
                                variant="body1"
                                marginY={2}
                                textAlign={"justify"}
                              >
                                We take an unwavering stance against all forms
                                of discrimination, including but not limited to
                                racism, sexism, homophobia, transphobia,
                                ableism, ageism, classism and body-shaming,
                                which reflects our total commitment to creating
                                a safe and welcoming space for everyone.
                              </Typography>
                            </React.Fragment>
                          }
                        >
                          <Button
                            sx={{
                              color: "#222",
                              padding: 0,
                              background: "transparent",
                              fontWeight: "400",
                              fontSize: "0.75rem",
                              lineHeight: "1.66",
                              minWidth: "auto",
                              textTransform: "none",
                              "&:hover": {
                                backgroundColor: "transparent",
                              },
                            }}
                            tabIndex={-1}
                            onMouseOver={handleOpen}
                            onTouchStart={handleOpen}
                            onClick={clickmore}
                          >
                            More Info
                          </Button>
                        </HtmlTooltip>

                        {errors.childPronouns && (
                          <Box
                            component={"span"}
                            marginLeft={1}
                            sx={{ color: "error.main" }}
                          >{`Child's pronouns are required`}</Box>
                        )}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
                {childPronouns === "4" && (
                  <Box display={"flex"}>
                    <TextField
                      id="standard-helperText"
                      variant="standard"
                      {...register("other1", { required: true })}
                      fullWidth
                      helperText={
                        other1 === "" && (
                          <Box
                            component={"span"}
                            sx={{ color: "error.main" }}
                          >{`Field is required`}</Box>
                        )
                      }
                      inputProps={{ maxLength: 50 }}
                      placeholder="Ze"
                    />

                    <Typography variant="body1" marginX={2}>
                      /
                    </Typography>

                    <TextField
                      id="standard-helperText"
                      variant="standard"
                      {...register("other2", { required: true })}
                      fullWidth
                      helperText={
                        other2 === "" && (
                          <Box
                            component={"span"}
                            sx={{ color: "error.main" }}
                          >{`Field is required`}</Box>
                        )
                      }
                      inputProps={{ maxLength: 50 }}
                      placeholder="Zir"
                    />

                    <Typography variant="body1" marginX={2}>
                      /
                    </Typography>

                    <TextField
                      id="standard-helperText"
                      variant="standard"
                      {...register("other3", { required: true })}
                      fullWidth
                      helperText={
                        other3 === "" && (
                          <Box
                            component={"span"}
                            sx={{ color: "error.main" }}
                          >{`Field is required`}</Box>
                        )
                      }
                      placeholder="Zir"
                    />
                  </Box>
                )}

                <Stack direction="row" spacing={2}>
                  <Controller
                    control={control}
                    name="date"
                    render={({ field }) => (
                      <FormControl fullWidth variant="standard">
                        <InputLabel>
                          Date
                          <Box
                            component={"span"}
                            sx={{ pl: 0.5, fontSize: 20 }}
                          >
                            *
                          </Box>
                        </InputLabel>
                        <Select
                          labelId="my-select-label"
                          id="my-select"
                          {...field}
                        >
                          {dates.map((date) => (
                            <MenuItem key={date} value={date}>
                              {date}
                            </MenuItem>
                          ))}
                        </Select>

                        <FormHelperText>
                          {/* <HtmlTooltip
                            open={open}
                            placement="bottom"
                            onClose={handleClose}
                            id="more-info"
                            title={
                              <React.Fragment>
                                <Typography
                                  sx={{
                                    fontStyle: "italic",
                                    padding: "0px 10px",
                                    fontSize: "0.9rem",
                                  }}
                                  variant="body1"
                                  marginY={2}
                                  textAlign={"justify"}
                                >
                                  At M:Tech we are inclusive and respectful to
                                  everybody.
                                </Typography>
                                <Typography
                                  sx={{
                                    fontStyle: "italic",
                                    padding: "0px 10px",
                                    fontSize: "0.9rem",
                                  }}
                                  variant="body1"
                                  marginY={2}
                                  textAlign={"justify"}
                                >
                                  We take an unwavering stance against all forms
                                  of discrimination, including but not limited
                                  to racism, sexism, homophobia, transphobia,
                                  ableism, ageism, classism and body-shaming,
                                  which reflects our total commitment to
                                  creating a safe and welcoming space for
                                  everyone.
                                </Typography>
                              </React.Fragment>
                            }
                          > */}
                          <Button
                            sx={{
                              color: "#222",
                              padding: 0,
                              background: "transparent",
                              fontWeight: "400",
                              fontSize: "0.75rem",
                              lineHeight: "1.66",
                              minWidth: "auto",
                              textTransform: "none",
                              "&:hover": {
                                backgroundColor: "transparent",
                              },
                            }}
                            tabIndex={-1}
                            // onMouseOver={handleOpen}
                            // onTouchStart={handleOpen}
                            // onClick={clickmore}
                          >
                            Date of Birth
                          </Button>
                          {/* </HtmlTooltip> */}
                          {errors.date && (
                            <Box
                              component={"span"}
                              marginLeft={1}
                              sx={{ color: "error.main" }}
                            >{`Date is required`}</Box>
                          )}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                  <Controller
                    control={control}
                    name="month"
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        variant="standard"
                        sx={{ marginY: 3 }}
                      >
                        <InputLabel id="my-select-label">
                          Month
                          <Box
                            component={"span"}
                            sx={{ pl: 0.5, fontSize: 20 }}
                          >
                            *
                          </Box>
                        </InputLabel>
                        <Select
                          labelId="my-select-label"
                          id="my-select"
                          {...field}
                        >
                          {months.map((month) => (
                            <MenuItem key={month} value={month}>
                              {month}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {errors.month && (
                            <Box
                              component={"span"}
                              sx={{ color: "error.main" }}
                            >{`Month is required`}</Box>
                          )}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />

                  <Controller
                    control={control}
                    name="year"
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        variant="standard"
                        sx={{ marginY: 3 }}
                      >
                        <InputLabel id="my-select-label">
                          Year
                          <Box
                            component={"span"}
                            sx={{ pl: 0.5, fontSize: 20 }}
                          >
                            *
                          </Box>
                        </InputLabel>
                        <Select
                          labelId="my-select-label"
                          id="my-select"
                          {...field}
                        >
                          {years.map((year) => (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {errors.year && (
                            <Box
                              component={"span"}
                              sx={{ color: "error.main" }}
                            >{`Year is required`}</Box>
                          )}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Stack>

                <Controller
                  name="currentYearGroup"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth variant="standard">
                      <InputLabel id="my-select-label">
                        {YearGroup?.details}
                        <Box component={"span"} sx={{ pl: 0.5, fontSize: 20 }}>
                          *
                        </Box>
                      </InputLabel>
                      <Select
                        labelId="my-select-label"
                        id="my-select"
                        {...field}
                      >
                        <MenuItem value="3">3</MenuItem>
                        <MenuItem value="4">4</MenuItem>
                        <MenuItem value="5">5</MenuItem>
                        <MenuItem value="6">6</MenuItem>
                        <MenuItem value="7">7</MenuItem>
                        <MenuItem value="8">8</MenuItem>
                      </Select>
                      <FormHelperText>
                        {errors.currentYearGroup && (
                          <Box component={"span"} sx={{ color: "error.main" }}>
                            {YearGroup?.details + ` is required`}
                          </Box>
                        )}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </FormControl>

              <Box display={"flex"} justifyContent="center">
                <Button
                  type="submit"
                  variant="contained"
                  style={{
                    background: "#ff00d0",
                    borderRadius: "10px",
                    fontWeight: "bold",
                  }}
                >
                  {activeStep.parentDetails ? (
                    isValidForm ? (
                      <Done />
                    ) : (
                      <Close />
                    )
                  ) : (
                    "Continue"
                  )}
                </Button>
              </Box>
            </form>
          </Box>
        </>
      ) : (
        ""
      )}
    </Box>
  );
};

export { StudentDetails };

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#ffffff",
    color: theme.palette.text.primary,
    width: "90%",
    maxWidth: 400,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
    boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
    marginLeft: "5%",
  },
}));
